<template>
  <div class="account-grid" :class="{ 'account-grid--dark': $vuetify.theme.dark }">
    <v-card v-for="card in allLiveAccounts" :key="card.id" flat class="account-card">
      <div class="account-card__header">
        <div class="account-card__identity">
          <span class="account-card__icon"><v-icon size="27">mdi-arrow-up-thin</v-icon></span>
          <div>
            <div class="account-card__type">
              <span class="account-card__badge">MT5</span>
              <strong>- {{ accountType(card) }}</strong>
            </div>
            <b>{{ card.trading_account_no || '—' }}</b>
          </div>
        </div>

        <div class="account-card__actions">
          <span class="account-card__status" :class="{ 'is-inactive': !isActive(card) }">
            <i />{{ isActive(card) ? 'Live' : 'Inactive' }}
          </span>
          <v-menu offset-y bottom left>
            <template v-slot:activator="{ attrs, on }">
              <v-btn icon small v-bind="attrs" v-on="on"><v-icon size="22">mdi-dots-vertical</v-icon></v-btn>
            </template>
            <v-list dense class="account-card__menu">
              <v-list-item v-for="item in items" :key="item.text" :to="menuRoute(item, card)">
                <v-list-item-title>{{ item.text }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </div>
      </div>

      <div class="account-card__metrics">
        <div><span>Balance</span><strong>{{ formatAmount(card.Balance) }}</strong></div>
        <div><span>Equity</span><strong>{{ formatAmount(card.Equity) }}</strong></div>
        <div><span>Free Margin</span><strong>{{ formatAmount(card.MarginFree) }}</strong></div>
        <div><span>Trades</span><strong>{{ numberValue(card.totalTrades).toLocaleString('en-US') }}</strong></div>
      </div>

      <div class="account-card__details">
        <div>
          <span class="account-card__flag">{{ currencyFlag(card.currency_name) }}</span>
          <p><small>MT5 Currency</small><strong>{{ card.currency_name || '—' }}</strong></p>
        </div>
        <div>
          <v-icon size="20">mdi-scale-balance</v-icon>
          <p><small>Leverage</small><strong>1:{{ card.leverage || '—' }}</strong></p>
        </div>
      </div>

      <v-btn
        :to="{ path: '/live-trade-details', query: { id: card.id } }"
        block outlined class="account-card__trades"
      >
        <i />See Live Trades
      </v-btn>
    </v-card>

    <div v-if="!allLiveAccounts || !allLiveAccounts.length" class="account-grid__empty">
      No live accounts found.
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex";

export default {
  name: "LiveAccountCards",

  data: () => ({
    items: [
      { text: "Deposit Funds", link: "/deposit" },
      { text: "Withdraw Funds", link: "/withdraw" },
      { text: "Internal Transfer", link: "/internal-transfer" },
      { text: "Change Trading Password", link: "/change-mt5-password", withAccount: true },
      { text: "Change Leverage", link: "/change-leverage", withAccount: true },
      { text: "Account Detail", link: "/trading-account-details" },
    ],
  }),

  computed: {
    ...mapState("trading", ["allLiveAccounts"]),
  },

  methods: {
    numberValue(value) {
      const number = Number(value);
      return Number.isFinite(number) ? number : 0;
    },

    formatAmount(value) {
      return `${this.numberValue(value).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} $`;
    },

    accountType(card) {
      return card.tradingaccounttypes?.account_type_display || card.trader_type || "Trading Account";
    },

    isActive(card) {
      return Number(card.status) === 1 && Number(card.is_live) === 1;
    },

    currencyFlag(currency) {
      const flags = { USD: "🇺🇸", EUR: "🇪🇺", GBP: "🇬🇧", AUD: "🇦🇺", CAD: "🇨🇦", JPY: "🇯🇵" };
      return flags[String(currency || "").toUpperCase()] || "🌐";
    },

    menuRoute(item, card) {
      return {
        path: item.link,
        query: {
          id: card.id,
          ...(item.withAccount ? { account: card.trading_account_no } : {}),
        },
      };
    },
  },
};
</script>

<style scoped>
.account-grid {
  --panel: #fff;
  --panel-soft: #f3f7ff;
  --border: #e0e3e7;
  --text: #15181d;
  --muted: #626973;
  --blue: #d99a00;
  --green: #09b66d;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
}
.account-grid--dark {
  --panel: #07111b;
  --panel-soft: #0b1621;
  --border: #293026;
  --text: #f5f7ff;
  --muted: #aeb6c0;
  --blue: #ffc400;
  --green: #13d987;
}
.account-card {
  min-width: 0;
  padding: 14px 16px 12px;
  border: 1px solid var(--border) !important;
  border-radius: 8px !important;
  background: linear-gradient(135deg, var(--panel), var(--panel-soft)) !important;
  color: var(--text) !important;
}
.account-card__header { display: flex; align-items: flex-start; justify-content: space-between; gap: 10px; }
.account-card__identity { display: flex; min-width: 0; align-items: center; gap: 12px; }
.account-card__icon { display: grid; width: 40px; height: 40px; flex: 0 0 40px; place-items: center; border-radius: 5px; background: rgba(38, 119, 255, .12); }
.account-card__icon .v-icon { color: var(--blue); }
.account-card__type { display: flex; min-width: 0; align-items: center; gap: 5px; color: var(--blue); font-size: 12px; }
.account-card__type strong { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.account-card__badge { padding: 1px 5px; border-radius: 3px; background: rgba(217, 154, 0, .13); color: var(--blue); font-size: 9px; font-weight: 800; }
.account-card__identity b { display: block; margin-top: 3px; color: var(--text); font-size: 12px; }
.account-card__actions { display: flex; align-items: center; gap: 5px; }
.account-card__actions .v-icon { color: var(--text); }
.account-card__status { display: inline-flex; padding: 5px 10px; align-items: center; gap: 5px; border-radius: 5px; background: rgba(9, 182, 109, .1); color: var(--green); font-size: 10px; font-weight: 700; }
.account-card__status i, .account-card__trades i { width: 6px; height: 6px; border-radius: 50%; background: currentColor; }
.account-card__status.is-inactive { background: rgba(255, 65, 83, .1); color: #ff4153; }
.account-card__metrics { display: grid; grid-template-columns: 1fr 1fr; gap: 14px 28px; margin: 18px 0 12px; }
.account-card__metrics div, .account-card__details p { display: flex; min-width: 0; flex-direction: column; }
.account-card__metrics span, .account-card__details small { color: var(--muted); font-size: 9.5px; }
.account-card__metrics strong { margin-top: 2px; color: var(--text); font-size: 13px; }
.account-card__details { display: grid; grid-template-columns: 1fr 1fr; padding: 10px 0; border-top: 1px solid var(--border); }
.account-card__details > div { display: flex; align-items: center; gap: 8px; }
.account-card__details > div + div { padding-left: 16px; border-left: 1px solid var(--border); }
.account-card__details .v-icon { color: var(--blue); }
.account-card__details strong { color: var(--text); font-size: 11px; }
.account-card__flag { font-size: 19px; line-height: 1; }
.account-card__trades { height: 29px !important; border-color: var(--blue) !important; border-radius: 3px !important; color: var(--blue) !important; font-size: 10px; font-weight: 650; letter-spacing: 0; text-transform: none; }
.account-card__trades i { display: inline-block; margin-right: 8px; color: #ff3152; }
.account-card__menu { min-width: 210px; }
.account-grid__empty { grid-column: 1 / -1; padding: 35px; border: 1px dashed var(--border); border-radius: 8px; color: var(--muted); text-align: center; }
@media (max-width: 1200px) { .account-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
@media (max-width: 720px) { .account-grid { grid-template-columns: 1fr; } }
@media (max-width: 380px) {
  .account-card__status { padding: 5px; }
  .account-card__metrics { gap: 12px; }
}
</style>
